import { CircleAlert, RotateCcw } from 'lucide-react';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { useSidebar } from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import ReactivateAccountForm from '../settings/forms/billing/ReactivateAccountForm';
import { isPendingSignup, hasSignupSubscription } from '../signup/activation';

export const DEACTIVATED_MESSAGE = 'This account is deactivated.';
export const RESELLER_DEACTIVATED_MESSAGE = `${DEACTIVATED_MESSAGE} Contact your provider to reactivate it.`;

/**
 * Deactivated-account notice for the new (sidebar) layout.
 * Resellers bill their own clients, so they get a neutral notice instead of
 * the Reactivate Account button.
 */
export function ReactivateAccountButton() {
  const [showForm, setShowForm] = React.useState(false);
  const auth = useSelector((state: any) => state.auth);
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  if (auth.activated || isPendingSignup(auth)) {
    return null;
  }

  if (hasSignupSubscription(auth)) {
    return <a href="mailto:support@chekkit.io" className="flex items-center gap-2 rounded-md border border-destructive/30 px-3 py-2 text-sm" aria-label="Get subscription help" title="Your existing subscription needs attention. Contact support@chekkit.io."><CircleAlert className="size-4 shrink-0" />{!isCollapsed && <span>Your subscription needs attention. Get help</span>}</a>;
  }

  if (auth.reseller) {
    return isCollapsed ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="status"
            aria-label={RESELLER_DEACTIVATED_MESSAGE}
            className="mx-auto flex size-8 items-center justify-center text-destructive"
          >
            <CircleAlert className="size-4" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="right">{RESELLER_DEACTIVATED_MESSAGE}</TooltipContent>
      </Tooltip>
    ) : (
      <p
        role="status"
        className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-foreground"
      >
        <CircleAlert className="mt-0.5 size-4 shrink-0 text-destructive" />
        {RESELLER_DEACTIVATED_MESSAGE}
      </p>
    );
  }

  return (
    <Dialog open={showForm} onOpenChange={setShowForm}>
      {isCollapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Button
                variant="destructive"
                size="icon"
                className="size-8 mx-auto"
                aria-label="Reactivate Account"
              >
                <RotateCcw className="size-4" />
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">{DEACTIVATED_MESSAGE} Reactivate Account</TooltipContent>
        </Tooltip>
      ) : (
        <div className="flex flex-col gap-1.5">
          <p role="status" className="px-1 text-sm text-muted-foreground">
            {DEACTIVATED_MESSAGE}
          </p>
          <DialogTrigger asChild>
            <Button variant="destructive" className="w-full">
              Reactivate Account
            </Button>
          </DialogTrigger>
        </div>
      )}
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden">
        <ReactivateAccountForm closeDialog={() => setShowForm(false)} />
      </DialogContent>
    </Dialog>
  );
}
